import { defineComponent as _defineComponent } from 'vue'

import { MetaInfo } from 'vue-meta/types/vue-meta';

const __default__ = {
	name: 'PopularPage',
	metaInfo() {
		return (this as unknown as { head: MetaInfo }).head;
	},
};

import { MonetizationType, PopularTitlesSorting, SraPlacement, TitleFilter } from '@/@types/graphql-types';
import { PAGINATION_LIMIT_BY_LEVEL } from '@/helpers/composables/useBiScroll';
import type { Content } from '@/interfaces/components/content';
import type { FilterCollection, FilterName } from '@/stores/filter.store';
import type { RoutingState } from '@/stores/routing.store';
import { isEqual } from 'apollo-utilities';

import { TitleList } from '@/lazy-load-components';

import ContentHeader from '@/components/content-header/ContentHeader.vue';
import SponsoredRecommendation from '@/features/sponsored-recommendations/components/SponsoredRecommendation.vue';

import { CollectionType } from '@/enums/collection-type';
import { TitleListLayout } from '@/enums/title-list-layout';
import { TitleOrder } from '@/enums/title-order';

import { toGraphqlFilters } from '@/helpers/graphql-model-helper';
import { handleStripeRedirect } from '@/helpers/pro-web-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { TitleEventPayloadGraphql, trackTitleEventGraphql } from '@/helpers/title-helper';

import { toastError } from '@/helpers/toast/toast-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import {
	GetPopularMetaDataDocument,
	GetPopularMetaDataQuery,
	GetPopularMetaDataQueryVariables,
	GetPopularTitlesDocument,
	GetPopularTitlesQuery,
	GetPopularTitlesQueryVariables,
} from '@/pages/graphql/queries/GetPopularTitles.query';

import { useSendBidId } from '@/features/sponsored-recommendations/composables/useSendBidId';
import { parsePageNumber } from '@/helpers/composables/usePagination';

import { useQuery } from '@/helpers/composables/useApollo';
import { hasQueryParamFilters, isAnyFilterActive, isFilterActive } from '@/helpers/filter-helper';
import { PopularTabType, TitleObjectType } from '@/interfaces/titles';

import { getSelectedTab } from '@/components/popular/provider-module/helper';

// need to import directly, otherwise we run into CLS issues.
// with <Suspense> in vue 3 we can better handle this, but for now it's too finicky.
import FilterBarSubgenrePills from '@/components/filter-bar/FilterBarSubgenrePills.vue';
import PopularFilterBar from '@/components/popular/provider-module/PopularFilterBar.vue';
import ProviderModule from '@/components/popular/provider-module/ProviderModule.vue';
import { useI18n } from '@/helpers/composables/useI18n';
import {
	useConstantStore,
	useFilterStore,
	useLanguageStore,
	useMetaStore,
	useRoutingStore,
	useUserStore,
} from '@/helpers/composables/useStores';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import {
	GetOrderedSubgenresDocument,
	GetOrderedSubgenresQuery,
	GetOrderedSubgenresQueryVariables,
} from './graphql/queries/GetSubgenres.query';

import { activeSubgenreCountries } from '@/constants/subgenres';
import { BI_SCROLL_TOP_OFFSET_PLACEMENT_ID } from '@/helpers/composables/useBiScroll';
import { useActivateExperiment, useExperiment } from '@/libraries/experiment';
import { useMounted } from '@vueuse/core';


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose }) {

const PopularTrendingTitles = () =>
	import(
		/* webpackChunkName: "jwtv-popular-trending-titles" */ '@/components/experiments/JwtvPopularExp/components/PopularTrendingTitles.vue'
	);

const FilterBar = () => import('@/components/filter-bar/FilterBar.vue');
const FilterBarSeo = () => import('@/components/filter-bar/seo/FilterBarSeo.vue');
const TitleListOptionsSeo = () => import('@/components/filter-bar/seo/TitleListOptionsSeo.vue');

const { tc } = useI18n();
const route = useRoute();

const fullPath = computed(() => route.fullPath);

const { sponsoredAd } = useSendBidId({
	pageType: 'VIEW_POPULAR',
	placement: SraPlacement.PopularView,
});

const showFooter = ref(false);
const requestPage = ref(getCurrentPage());

// the same as `activeRoute` but only changes when the new page is also CollectionType.POPULAR.
// it doesn't change, unlike `activeRoute`, when one leaves the popular page.
// this way we can circument re-renders in the background.
const popularRoute = ref<RoutingState['activeRoute'] | null>(null);

const { filters, selectedProviders } = useFilterStore();
const filterPopular = computed(() => filters.value(CollectionType.POPULAR));

// EXP_JWTV_POPULAR_PAGE
const { experimentInfo } = useExperiment('EXP_JWTV_POPULAR_PAGE');
const isMounted = useMounted();
const activatePopularExp = computed(() => {
	const urlFilters: FilterName[] = [
		'content_types',
		'sort_asc',
		'sorting_random_seed',
		'subgenres',
		...((experimentInfo.value.variant === 'variant_3' ? ['monetization_types', 'sort_by'] : []) as FilterName[]),
	];

	const filters: FilterName[] = (Object.keys(filterPopular.value) as FilterName[]).filter(
		item => !urlFilters.includes(item)
	);

	const hasOtherFiltersActive = isAnyFilterActive(filters, filterPopular.value);
	// do not activate variant 3 for logged in users
	const v3ActivationCondition = experimentInfo.value.variant === 'variant_3' ? !isLoggedIn.value : true;

	return (
		isMounted.value &&
		!hasOtherFiltersActive &&
		v3ActivationCondition &&
		['app.titles.popular.list', 'app.titles.popular.movie', 'app.titles.popular.show'].includes(
			popularRoute.value?.name
		)
	);
});
const { activeVariant } = useActivateExperiment('EXP_JWTV_POPULAR_PAGE', activatePopularExp);
const showTrendingTitles = computed(() => activeVariant.value === 'variant_1' || activeVariant.value === 'variant_2');
// EXP_JWTV_POPULAR_PAGE

const { language, country } = useLanguageStore();

const { queryParams, activeRoute, lastRoute, activeDomain } = useRoutingStore();

const {
	isHideSeenDislikedTitlesEnabled,
	isLoggedIn,
	jwLoginId,
	setPermanentAudiencesSubgenres,
	resetPermanentAudiencesSubgenres,
} = useUserStore();

const { paginationLevel, setPagination } = useMetaStore();

const content = ref<Content | null>(null);

const searchText = ref('');

const isProviderPage = computed(
	() =>
		!!popularRoute.value?.name?.startsWith('app.titles.popular.provider') &&
		!!selectedProvider.value &&
		isProviderPagesEnabled.value
);

const hasPaginationImplementation = computed(() => {
	// hard page limit through levels is happening as well on InfiniteBiScroll.vue:40
	const hardLimit = PAGINATION_LIMIT_BY_LEVEL[paginationLevel.value] ?? 0;
	const isWithinHardLimit = getCurrentPage() <= hardLimit;
	const hasQueryParams = hasQueryParamFilters(filterPopular.value);
	return paginationLevel.value > 0 && isWithinHardLimit && !hasQueryParams;
});

// Generic Title List
const graphqlFilters = computed<TitleFilter>(() =>
	toGraphqlFilters(
		filterPopular.value,
		selectedProviders.value(!isLoggedIn.value),
		isHideSeenDislikedTitlesEnabled.value
	)
);

// Sort By options from GraphQL are Uppercase, but tracking expects lowercase TODO [Graphql] remove when get rid of REST
const RestToGraphQLSortingMap: Partial<{ [key in TitleOrder]: PopularTitlesSorting }> = {
	[TitleOrder.POPULARITY]: PopularTitlesSorting.Popular,
	[TitleOrder.TRENDING]: PopularTitlesSorting.Trending,
	[TitleOrder.TMDB_POPULARITY]: PopularTitlesSorting.TmdbPopularity,
	[TitleOrder.ALPHABETICAL]: PopularTitlesSorting.Alphabetical,
	[TitleOrder.RANDOM]: PopularTitlesSorting.Random,
	[TitleOrder.RELEASE_YEAR]: PopularTitlesSorting.ReleaseYear,
	[TitleOrder.RELEVANCE]: PopularTitlesSorting.Relevance,
	[TitleOrder.SCORE_IMDB]: PopularTitlesSorting.ImdbScore,
	[TitleOrder.RUNTIME]: PopularTitlesSorting.Runtime,
};
const sortBy = computed<PopularTitlesSorting>(() => {
	// To fetch titles on server side we need to get sort by option from url params
	if (queryParams.value.sort_by) {
		return RestToGraphQLSortingMap[queryParams.value.sort_by as TitleOrder] ?? PopularTitlesSorting.Popular;
	}

	if (filterPopular.value.sort_by) {
		return RestToGraphQLSortingMap[filterPopular.value.sort_by as TitleOrder] ?? PopularTitlesSorting.Popular;
	}

	return PopularTitlesSorting.Popular;
});

// Popular Page Data
/**
 * Popular page initially renders with "page 0" for a fast first experience.
 * On first scroll we then fetch the "page 1", this triggers loading meaning
 * the whole grid disappears for a spinner upon first interaction.
 *
 * This variable is to have an exception for that first load.
 */
const isFirstPageLoaded = ref(false);

const randomSeed = computed(() => {
	const isRandomSort = sortBy.value === PopularTitlesSorting.Random;

	if (process.server) {
		const randomSeed = Number(queryParams.value.sorting_random_seed) || 1;
		return isRandomSort ? randomSeed : 0;
	}

	const randomSeed = filterPopular.value.sorting_random_seed || 1;
	return isRandomSort ? randomSeed : 0;
});

const queryVariables = computed<GetPopularTitlesQueryVariables>(() => {
	// SEO benefits from more titles per page but that hurts perf for returning users
	// plus, Google started scraping (unnecessarily) pages with query params that take much longer SSR'ing on-demand,
	// so Google adds the longer request times to its statistics and crawls us less.
	const hasQueryParams = hasQueryParamFilters(filterPopular.value);
	const loggedOutAmount = hasQueryParams ? 100 : 40;
	const first = isLoggedIn.value ? 40 : loggedOutAmount;
	const offset = (requestPage.value - 1) * first;

	return {
		first,
		offset: hasPaginationImplementation.value ? offset : null, // use offset for new <InfiniteBiScroll> implementation
		after: '', // use cursor for old <InfiniteScroll> implementation
		popularTitlesFilter: { ...graphqlFilters.value, searchQuery: searchText.value },
		watchNowFilter: {
			packages: graphqlFilters.value.packages,
			monetizationTypes: graphqlFilters.value.monetizationTypes as MonetizationType[],
		},
		popularTitlesSortBy: sortBy.value,
		language: language.value,
		country: country.value,
		sortRandomSeed: randomSeed.value,
	};
});

const popularPageDataQuery = useQuery<GetPopularTitlesQuery, GetPopularTitlesQueryVariables>(
	GetPopularTitlesDocument,
	queryVariables,
	{
		fetchPolicy: 'cache-and-network',
		enabled: !isProviderPage.value,
	}
);
const popularPageData = computed(() => popularPageDataQuery.result.value?.popularTitles);

const metaStore = useMetaStore();

popularPageDataQuery.onResult(({ data }) => {
	isFirstPageLoaded.value = true;

	if (data && hasPaginationImplementation.value) {
		updatePaginationMeta(queryVariables.value.offset ?? 0);
	}
	if (data) {
		metaStore.setPopularPageJsonLD(data.popularTitles);
	}
});
popularPageDataQuery.onError(e => {
	if (process.client) {
		toastError({ message: tc('WEBAPP_ALERT_ERROR_TITLE'), duration: 2000 });
	}

	captureMessageForSentry('[GraphQL Popular Page Data Query]:', { error: e, where: 'Component: Popular' }, 'error');
});
watch(
	() => isLoggedIn.value,
	() => popularPageDataQuery.refetch()
);

// Genres With Subgenres
const showSubgenresFilter = computed(() => activeSubgenreCountries.includes(country.value));

const genresWithSubgenresQuery = useQuery<GetOrderedSubgenresQuery, GetOrderedSubgenresQueryVariables>(
	GetOrderedSubgenresDocument,
	() => ({
		country: country.value,
		language: language.value,
	}),
	() => ({ enabled: showSubgenresFilter.value })
);
const genresWithSubgenres = computed(() => genresWithSubgenresQuery.result.value);
const orderedSubgenres = computed(() => genresWithSubgenres.value?.genres);
const subgenresList = computed(() => genresWithSubgenres.value?.subgenres);

const activeSubgenreTitle = computed(() => {
	if (!isSubgenreActive.value || filterPopular.value.subgenres.length !== 1) return '';

	const activeSubgenre = filterPopular.value.subgenres[0];
	return subgenresList.value?.find(sub => sub.content.shortName === activeSubgenre)?.content.name ?? '';
});

// Popular Page Metadata
const isSubgenreActive = computed(() => showSubgenresFilter.value && filterPopular.value.subgenres.length > 0);

const popularPageMetaDataQuery = useQuery<GetPopularMetaDataQuery, GetPopularMetaDataQueryVariables>(
	GetPopularMetaDataDocument,
	() => ({ fullPath: route.fullPath, site: activeDomain.value }),
	() => ({ enabled: isSubgenreActive.value })
);
const popularPageMetaData = computed(() => popularPageMetaDataQuery.result.value?.metaData);

watch(
	() => activeRoute.value,
	route => {
		const { collectionType } = route?.meta as any;
		if (collectionType === CollectionType.POPULAR) {
			popularRoute.value = route;
		}
	},
	{ immediate: true }
);

// when leaving page through route
watch(
	() => activeRoute.value,
	() => {
		if (!activeRoute.value?.name?.includes('popular')) onLeavePopularPage();
	}
);

// Permanent Audiences for Subgenres
const previousPermanentAudience = ref('');

const head = computed<MetaInfo>(() => {
	// update meta title for subgenre pages - title count disparity from content
	if (!isSubgenreActive.value || !popularPageMetaData.value?.metaTitle || !titleAmount.value) return {};

	const metaTitleWithTitleCount = popularPageMetaData.value?.metaTitle.replace(
		'{{FE-TitleCount}}',
		String(titleAmount.value)
	);

	return {
		title: metaTitleWithTitleCount,
		meta: [
			{ vmid: 'title', name: 'title', content: metaTitleWithTitleCount },
			{ vmid: 'og:title', property: 'og:title', content: metaTitleWithTitleCount },
		],
	};
});

const permanentAudienceSubgenres = computed(() =>
	isSubgenreActive.value
		? (
				(subgenresList.value ?? []).find(sub => sub.content.shortName === filterPopular.value.subgenres![0])
					?.id ?? ''
		  ).replace('tl-pa-', 'pa:')
		: ''
);

const setPermanentAudienceSubgenre = computed(
	() =>
		isSubgenreActive.value &&
		!!permanentAudienceSubgenres.value &&
		// prevent duplicate events firing when non-subgenre filters are changed on Popular page
		permanentAudienceSubgenres.value !== previousPermanentAudience.value &&
		!!activeRoute.value?.name?.includes('popular')
);

watch(
	fullPath,
	() => {
		if (!isSubgenreActive.value) {
			resetPermanentAudiencesSubgenres();
			previousPermanentAudience.value = '';
		}
		if (setPermanentAudienceSubgenre.value) {
			setPermanentAudiencesSubgenres([permanentAudienceSubgenres.value]);
			previousPermanentAudience.value = permanentAudienceSubgenres.value;
		}
	},
	{ immediate: true }
);

// when leaving page through modal
onBeforeUnmount(() => onLeavePopularPage());

function onLeavePopularPage() {
	resetPermanentAudiencesSubgenres();
	previousPermanentAudience.value = '';
}

const isProviderPagesEnabled = computed(
	() => ['en', 'fr', 'de', 'it', 'pt', 'es', 'ar'].includes(language.value) && !isLoggedIn.value
);

// check if from last route and current route, there was a change in single-provider pages with its ProviderModule.
// this tells us that since the last route we needed to update the single-provider page: <ProviderModule>.
// either by adding it to the popular page, or removing it.
// watching this change lets us skip unnecessary rerenders.
const isProviderPageChanged = computed(() => {
	const lastPageIsProviderPage =
		!!lastRoute.value?.name?.startsWith('app.titles.popular.provider') && isProviderPagesEnabled.value;
	const currentPageIsProviderPage = isProviderPage.value;
	return lastPageIsProviderPage !== currentPageIsProviderPage;
});

const { providersByShortName } = useConstantStore();
const selectedProvider = computed(() => providersByShortName.value[filterPopular.value.providers[0]]);

const selectedTab = computed(() => {
	if (isFilterActive('content_types', TitleObjectType.MOVIE, filterPopular.value)) {
		return PopularTabType.MOVIE;
	}

	if (isFilterActive('content_types', TitleObjectType.SHOW, filterPopular.value)) {
		return PopularTabType.SHOW;
	}

	return PopularTabType.ALL;
});

function getCurrentPage() {
	const page = (route.query.page as unknown as string) || '1';
	return parsePageNumber(page);
}

function search(searchQuery: string) {
	searchText.value = searchQuery;
}

const isLoading = computed(() => !isFirstPageLoaded.value && popularPageDataQuery.loading.value);

// PERCEIVED LOADING START
const perceivedLoading = ref(false);

// usually popularPageDataQuery wouldn't change when graphql requests have been requested already.
// but since we're fetching sponsoredAdQuery upon every pageview, popularPageDataQuery will change and perceivedLoading will change, too.
watch(
	() => isLoading.value,
	showingSpinner => {
		// just to be sure, that SSR is never rendering the loading state
		if (process.server) return;

		// add a small time, so the vue runtime gets time to render the loading state.
		// if there is no loading state, vue will have to update the existing components with the new virtual DOM. this is more compute intense.
		if (showingSpinner) perceivedLoading.value = true;

		// using setTimeout instead of $nextTick, because $nextTick will be optimised by Vue into one render.
		// but we need to force an additional render with the loading state.
		if (!showingSpinner) setTimeout(() => (perceivedLoading.value = false), 0);
	}
);
// PERCEIVED LOADING START

const popularTitles = computed(() => popularPageData.value?.edges || []);
const titleAmount = computed(() => popularPageData.value?.totalCount ?? 0);
const isFirstPage = computed(() => !popularPageData.value?.pageInfo.hasPreviousPage);
const isAtEndOfList = computed(() => !popularPageData.value?.pageInfo.hasNextPage);

// SUBGENRES //

watch(filterPopular, (newValue: FilterCollection, oldValue: FilterCollection) => {
	if (!isProviderPage.value) searchText.value = '';

	if (!isEqual(newValue, oldValue)) {
		isFirstPageLoaded.value = false;

		// to separate `page` query param and only set `requestPage` when applicable and needed.
		// otherwise we'd do fresh request whenever the `page` query parameter changes (due to <InfiniteBiScroll>).
		requestPage.value = 1;
	}

	if (!isEqual(newValue.providers, oldValue.providers)) {
		searchText.value = '';
	}
});

const router = useRouter();
onMounted(() => {
	if (route.query['jw-pro']) handleStripeRedirect(route, router, jwLoginId.value);

	// track first page view with permanent Audiences here, after subgenreList fetched in beforeEnter routing/config
	if (setPermanentAudienceSubgenre.value && (activeRoute.value?.name || '').includes('subgenre')) {
		TrackingHelper.trackPage(fullPath.value);
		previousPermanentAudience.value = permanentAudienceSubgenres.value;
	}
});

// it's possible, that the `page` param is empty due to old <InfiniteScroll> implementation.
function load(event: any, page?: number | undefined, direction: 'prev' | 'next' = 'next') {
	if (event == null) return;

	const offset = hasPaginationImplementation.value ? (page! - 1) * queryVariables.value.first! : null;
	const after = hasPaginationImplementation.value ? null : popularPageData.value?.pageInfo.endCursor;

	const popularPageDataQueryFetchMore = popularPageDataQuery.fetchMore({
		variables: { offset, after },
		updateQuery(previousResult, { fetchMoreResult }) {
			if (!fetchMoreResult) return previousResult;

			// either append or prepend the fetched titles
			const previousEdges = previousResult.popularTitles.edges ?? [];
			const fetchMoreEdges = fetchMoreResult.popularTitles.edges ?? [];
			const edges =
				direction === 'next' ? [...previousEdges, ...fetchMoreEdges] : [...fetchMoreEdges, ...previousEdges];

			return {
				popularTitles: {
					__typename: previousResult.popularTitles.__typename,
					edges,
					pageInfo: fetchMoreResult.popularTitles.pageInfo,
					totalCount: fetchMoreResult.popularTitles.totalCount,
				},
			};
		},
	});

	if (popularPageDataQueryFetchMore == null) return;

	popularPageDataQueryFetchMore
		.catch(e => {
			captureMessageForSentry(
				'[GraphQL Popular Page Load More Titles Issue]:',
				{ error: e, where: 'Component: Popular, method: onLoadNext' },
				'error'
			);
		})
		.finally(() => {
			if (hasPaginationImplementation.value) updatePaginationMeta(offset || 0);

			event.target?.complete?.(popularTitles.value.length);
		});
}

function trackClick(payload: TitleEventPayloadGraphql) {
	if (searchText.value) {
		TrackingHelper.trackEvent('search_suggest_click_lower', {
			action: getSelectedTab(activeRoute.value?.name as string),
			label: searchText.value,
			property: selectedTab.value,
		});
	}

	trackTitleEventGraphql('title_clicked', payload, 'popular-timeline');
}

function scrollToTop() {
	content.value?.scrollToTop(0, 'auto');
}

function updatePaginationMeta(offset: number) {
	const page = offset / queryVariables.value.first!;
	const prev = page > 1 ? page - 1 : null;
	const next = page + 1;
	const paginationData = { prev, next, page };
	setPagination(paginationData);
}

expose({ head });

return { __sfc: true,PopularTrendingTitles, FilterBar, FilterBarSeo, TitleListOptionsSeo, tc, route, fullPath, sponsoredAd, showFooter, requestPage, popularRoute, filters, selectedProviders, filterPopular, experimentInfo, isMounted, activatePopularExp, activeVariant, showTrendingTitles, language, country, queryParams, activeRoute, lastRoute, activeDomain, isHideSeenDislikedTitlesEnabled, isLoggedIn, jwLoginId, setPermanentAudiencesSubgenres, resetPermanentAudiencesSubgenres, paginationLevel, setPagination, content, searchText, isProviderPage, hasPaginationImplementation, graphqlFilters, RestToGraphQLSortingMap, sortBy, isFirstPageLoaded, randomSeed, queryVariables, popularPageDataQuery, popularPageData, metaStore, showSubgenresFilter, genresWithSubgenresQuery, genresWithSubgenres, orderedSubgenres, subgenresList, activeSubgenreTitle, isSubgenreActive, popularPageMetaDataQuery, popularPageMetaData, previousPermanentAudience, head, permanentAudienceSubgenres, setPermanentAudienceSubgenre, onLeavePopularPage, isProviderPagesEnabled, isProviderPageChanged, providersByShortName, selectedProvider, selectedTab, getCurrentPage, search, isLoading, perceivedLoading, popularTitles, titleAmount, isFirstPage, isAtEndOfList, router, load, trackClick, scrollToTop, updatePaginationMeta, TitleList, ContentHeader, SponsoredRecommendation, CollectionType, TitleListLayout, FilterBarSubgenrePills, PopularFilterBar, ProviderModule, BI_SCROLL_TOP_OFFSET_PLACEMENT_ID }
}

})